import { pagedQueryFields } from "./paging.js";
import { podPhaseView } from "./pod-phase.js";
import { z } from "zod";
//#region src/app-pods.ts
/**
* App-scoped pod drill-down wire types — `GET /v1/admin/apps/:appId/dps/:dpId/pods`
* (site-admin-api observability module; design/02 §2.2 **E3**).
*
* This is the endpoint that makes `kubectl` unnecessary: for one app on one DP,
* what containers exist, are they up, and — the point of the endpoint — what is
* WRONG with them.
*
* ## What R4 ships, and what it does not (ruling **R4-03**)
*
* **OUT, because there is no wire producer:**
*   - `cpuRequestMillis` / `cpuLimitMillis` / `memoryRequestBytes` /
*     `memoryLimitBytes`. `PodSummarySchema` has no `resources` and
*     `PodContainerStatusSchema` has no requests/limits, so these could only be
*     populated by inventing them. They move with E5's DP release.
*   - `'Unschedulable'` — ✅ NOW SHIPPED (compute-classes D7). It needed a DP
*     release behind it, exactly as this note said, and that release provides
*     `PodSummarySchema.unschedulable`. Seven problem values in design/02,
*     **seven** here.
*   - `cpuRequestMillis` / `cpuLimitMillis` / `memoryRequestBytes` /
*     `memoryLimitBytes` — still OUT. `PodSummarySchema` has no `resources` and
*     `PodContainerStatusSchema` has no requests/limits, so these could only be
*     populated by inventing them. Unchanged by compute-classes.
*
* The remaining omission is deliberate and written down. Do not "complete" this
* shape against design/02 without a DP release behind it.
*
* ## Absence has three renderings, and this contract keeps them distinct
*
* `empty` ≠ `errorstate` ≠ `notcollected` is the canon the whole release exists
* to establish, and every nullable field below is nullable for a stated reason
* rather than for convenience:
*   - `problems: []` means **checked, nothing wrong** — never "we didn't look".
*   - `ageSeconds: null` means the DP did not report an age (deploy skew), NOT
*     "zero seconds old". Coalescing to `0` would render a week-old pod as
*     brand new, which is why the producer refuses to fabricate it.
*   - `nodeName: null` is DIAGNOSTIC, not missing data: an unscheduled pod has
*     no node yet, and that absence is itself the signal.
*   - `attribution` is a discriminated union so `notcollected` can never be
*     confused with "this pod carries no app label" (see {@link podAttributionView}).
*/
/**
* The container-derivable problem states, plus one pod-level state.
*
* ✅ `'Unschedulable'` IS NOW PRESENT, and the omission note it replaces was RIGHT at the time:
* this value lives on the pod CONDITION, `conditions[]` is not on the wire `PodSummarySchema`, and
* adding the member without a producer would have created an enum member nothing could ever emit.
* The producer now exists — `PodSummarySchema.unschedulable`, a single boolean
* (compute-classes D7) — and the derivation is in
* `site-admin-api/src/modules/observability/pod-problems.ts` beside the other six.
*
* ⛔ IT IS APPENDED, NOT INSERTED. Enum order is the emission order {@link appPodSummarySchema}
* guarantees, so `problems[]` is stable enough to snapshot; inserting anywhere else rewrites every
* snapshot for cosmetics.
*
* ⚠️ IT IS ALSO THE ONLY MEMBER THAT IS NOT CONTAINER-DERIVED, which matters for the producer: the
* other six are read off `containerStatuses[]` and an unschedulable pod has NO container statuses at
* all. A producer that iterates containers to build `problems[]` will emit an empty array for the
* exact pod this member exists to describe. Derive it from the pod-level field, before the container
* loop.
*
* ⚠️ ABSENT `unschedulable` (deploy skew) MUST NOT PRODUCE THIS MEMBER. `undefined` means "this DP
* did not report", not "schedulable" — emitting the problem on absence would flag every pod on an
* un-upgraded DP.
*/
const POD_PROBLEMS = [
	"CrashLoopBackOff",
	"ImagePullBackOff",
	"ErrImagePull",
	"CreateContainerConfigError",
	"OOMKilled",
	"NotReady",
	"Unschedulable"
];
const podProblemEnum = z.enum(POD_PROBLEMS);
/**
* A container's PREVIOUS state — the "why did it restart" record. Mirrors the
* wire `PodContainerLastStateSchema` field for field.
*
* ABSENT ⇔ the container has never previously terminated, which is the normal
* case for most containers. Absence is not an error.
*/
const appPodContainerLastStateSchema = z.object({
	/** `running` | `waiting` | `terminated`. Never `unknown`. */
	state: z.string(),
	/** e.g. `OOMKilled`, `Error`, `Completed`. Absent when kubelet reported none. */
	reason: z.string().optional(),
	/** ⚠️ `0` is MEANINGFUL (clean exit). Discriminate with `!== undefined`, never truthiness. */
	exitCode: z.number().int().optional(),
	/** `lastState.terminated.finishedAt`, verbatim as k8s reported it. */
	finishedAt: z.string().optional()
}).strict();
/** One container's status. Field-for-field the wire `PodContainerStatusSchema`. */
const appPodContainerSchema = z.object({
	name: z.string(),
	ready: z.boolean(),
	restartCount: z.number().int().nonnegative(),
	/** `running` | `waiting` | `terminated` | `unknown`. */
	state: z.string(),
	/** Fully-qualified image ref including tag/digest. Absent until k8s reports it. */
	image: z.string().optional(),
	/**
	* The CURRENT state's reason — `state.waiting.reason` (e.g. `CrashLoopBackOff`)
	* or `state.terminated.reason` (e.g. `OOMKilled`).
	*
	* ⚠️ **ABSENT IS THE NORMAL, HEALTHY CASE.** `state.running` has no `reason`
	* field at all, so every healthy running container omits this. Rendering
	* absence as an error is the `errorstate`-as-`empty` failure this release
	* exists to remove (R4-03). Consumers fall back to the `state` name.
	*/
	reason: z.string().optional(),
	/** `state.terminated.exitCode`. ⚠️ `0` is MEANINGFUL and is not the same as absent. */
	exitCode: z.number().int().optional(),
	lastState: appPodContainerLastStateSchema.optional()
}).strict();
/**
* Per-pod app/service/version attribution, as a DISCRIMINATED UNION rather than
* an optional object.
*
* ⛔ This shape exists because of **#971 / R4-12**: WS-C (the attribution
* labels) has not been released, so the DP sends `attribution: {}` for pods it
* has not labelled. An empty object is indistinguishable from "this pod really
* has no app label", and rendering it blank tells an app owner their pod is
* unattributed when in truth nothing has looked yet. **Until WS-C ships, that
* case renders `notcollected`, never blank.**
*
* Note the D10 "Other pods" case cannot arise here: every pod E3 returns has
* already been app-scoped by its name prefix, so it demonstrably belongs to the
* app. `{}` inside this endpoint means "labels not stamped", full stop.
*/
const podAttributionView = z.discriminatedUnion("state", [z.object({
	state: z.literal("collected"),
	/** ← `zion.witness.ai/app`. */
	app: z.string().optional(),
	/** ← `zion.witness.ai/fixture`. */
	fixture: z.string().optional(),
	/** ← `zion.witness.ai/version`; the preferred version source. */
	version: z.string().optional(),
	/** ← `app.kubernetes.io/name`. */
	serviceName: z.string().optional()
}).strict(), z.object({
	state: z.literal("notcollected"),
	/** Why nothing was collected — names the tracking issue, so the UI need not. */
	reason: z.string()
}).strict()]);
/** One pod in the app's scope on one DP. */
const appPodSummarySchema = z.object({
	name: z.string(),
	namespace: z.string(),
	/**
	* k8s pod phase — `Pending` | `Running` | `Succeeded` | `Failed` | `Unknown`.
	*
	* ⛔ **ENUMERATED, WITH A DEGRADING FALLBACK, NOT A BARE ENUM (#1994).** This docblock used to
	* enumerate five values over a `z.string()`, so `phase: 'Nonsense'` parsed clean. It is now
	* {@link podPhaseView}: an unrecognised string maps to `'Unknown'` rather than rejecting the
	* pod, because a sixth kubelet phase must cost one under-described row and not a 500. A
	* MISSING key, a `null` and a non-string are still REJECTED, exactly as before.
	*/
	phase: podPhaseView,
	/**
	* Parsed from the pod name against the deterministic naming spine
	* (`<slug>--<fixtureId>--<versionSlug>-<rs>-<pod>`). `null` when the name
	* does not carry that part — a legitimately-scoped pod may be named from
	* the shorter `instanceKey` form, which has no version.
	*/
	fixtureId: z.string().nullable(),
	versionSlug: z.string().nullable(),
	/**
	* Real pod age in whole seconds. **`null`, not `0`, when the DP did not
	* report one** (deploy skew) — `0` would render a week-old pod as brand new.
	*/
	ageSeconds: z.number().int().nonnegative().nullable(),
	/** Sum of `restartCount` across every container. */
	restarts: z.number().int().nonnegative(),
	/**
	* `spec.nodeName`. **`null` is diagnostic, not missing:** an unscheduled pod
	* has no node yet and the absence is the signal.
	*/
	nodeName: z.string().nullable(),
	/**
	* compute-classes D7 — the pod-level unschedulable condition. Optional; ABSENT ≠ FALSE.
	*
	* ⚠️ `.optional()` rather than `.nullable()` like its `nodeName` neighbour, deliberately:
	* `nodeName: null` is the DP saying "no node yet", whereas an absent `unschedulable` is the
	* DP saying nothing at all (a build predating WS-30 CG-13). Those are different facts and the
	* two encodings keep them apart.
	*/
	unschedulable: z.boolean().optional(),
	attribution: podAttributionView,
	containers: z.array(appPodContainerSchema),
	/**
	* What is wrong with this pod, derived from its containers.
	*
	* **A SET, not a pick.** A container can match more than one problem and
	* both are kept: an OOM-driven crashloop is `['CrashLoopBackOff','OOMKilled']`
	* and dropping either destroys the diagnosis. De-duplicated across the pod's
	* containers, emitted in {@link POD_PROBLEMS} order.
	*
	* `[]` means **checked, nothing wrong** — it never means "not evaluated".
	*/
	problems: z.array(podProblemEnum)
}).strict();
/**
* Page-size ceiling for a PAGED pod read.
*
* ⚠️ Deliberately NOT the same number as `AppPodsService.MAX_PODS` (500) and
* deliberately not unified with it (R-25): `MAX_PODS` is the **hard ceiling on
* what this server will assemble at all** — pods beyond it are on no page — while
* this bounds **one page** of what it did assemble. Two different questions; the
* response answers both through `truncated` + `nextCursor`.
*/
const APP_PODS_MAX_LIMIT = 200;
/**
* ⛔ Consumed by the TOOL only (`pagedToolFields`) and by the SERVICE's paging
* branch. ⛔ It is NOT a `.default()` on the route schema — see R-26 and the
* `appPodsQuerySchema` docblock. V-1's "default ≤ 100" is an agent-context rule,
* not a route rule.
*/
const APP_PODS_DEFAULT_LIMIT = 100;
/**
* Filterable query params. `.strict()` (#466) rejects unknown keys.
*
* ⛔⛔ **`limit` IS `.optional()` WITH NO `.default()` (DECISIONS R-26).** This
* schema is bound at the controller AND spread into the `get_app_pods` tool's
* args, and the live `cp-admin-ui` pods caller
* (`src/api/appPods.api.ts:51-61` — `PodsQuery` has no `limit` and no `cursor`
* field, and `podsPath` never emits one) reaches the ROUTE and sends no `limit`.
* A default here would silently drop that operator debugging surface from
* up-to-500 pods to the default. The **tool** row spreads `pagedToolFields` on
* top; the **service** applies the default only inside its paging branch. Three
* layers, one rule: ⛔ the default lives with the tool, never with the route.
*/
const appPodsQuerySchema = z.object({
	/** Narrow to one fixture's pods. Validated against the instance-id grammar server-side. */
	fixtureId: z.string().min(1).max(253).optional(),
	/** Narrow to one deployed version. Requires `fixtureId` — the scope is hierarchical. */
	versionSlug: z.string().min(1).max(16).optional(),
	/**
	* Include pods in a terminal phase (`Succeeded` / `Failed`). Default
	* **false**: a completed Job pod is not a problem, and listing every one
	* ever run buries the pods that matter.
	*
	* `z.coerce.boolean()` is deliberately NOT used — it maps the STRING
	* `'false'` to `true` (every non-empty string is truthy), so `?includeTerminated=false`
	* would turn the filter on. An explicit two-value enum is the only form
	* that cannot silently invert.
	*/
	includeTerminated: z.enum(["true", "false"]).optional(),
	/**
	* ⭐ **V-1 over a LIVE list, and the honesty caveat is in the tool
	* description (C-4).** The cursor is a pod NAME, not a snapshot id: the pod
	* set is re-read from the cluster on every page, so a pod created mid-walk
	* with a name earlier than the cursor **will be missed on this walk**, and a
	* pod deleted mid-walk simply is not there. That is stated rather than
	* papered over — the alternative (a server-side snapshot) is state this
	* service does not keep and should not start keeping for a debugging read.
	*/
	...pagedQueryFields({ maxLimit: 200 })
}).strict();
/** The pod page for one app on one DP. */
const appPodsResponseSchema = z.object({
	/** Echoes the `:dpId` path param verbatim. */
	dpId: z.uuid(),
	/** Echoes the `:appId` path param verbatim — the app **UUID**, not the slug. */
	appId: z.uuid(),
	/** The DP namespace these pods live in, as resolved server-side from `dpId`. */
	namespace: z.string(),
	pods: z.array(appPodSummarySchema),
	/**
	* ⛔ **More pods existed than you received — for EITHER cause** (DECISIONS
	* R-25): the app's pod set hit the server's hard ceiling
	* (`AppPodsService.MAX_PODS`) before paging, OR this page has a remainder.
	* Formally `truncated = ceilingHit || morePages`.
	*
	* ⭐ **`nextCursor` carries the REMEDY, and the pair is what disambiguates
	* the two causes** — there is deliberately no third flag:
	*   • `truncated && nextCursor !== null` ⇒ continue the walk.
	*   • `truncated && nextCursor === null` ⇒ ⛔ an UNCONTINUABLE cap: pods
	*     exist that this endpoint will not serve on ANY page. Narrow with
	*     `fixtureId`/`versionSlug`, or accept an incomplete answer.
	*   • `!truncated` ⇒ you have every pod in scope.
	*
	* ⛔ The full equivalence `truncated ≡ (nextCursor !== null)` does NOT hold
	* here — only the one-directional `nextCursor !== null ⇒ truncated`. This is
	* one of the **TWO** capped routes on the surface (`my_grants` is the other —
	* its cap is `RESOURCE_GRANT_EXPANSION_CAP`); every uncapped route is a full
	* equivalence (R-25, R-32). **Never silently cap.**
	*/
	truncated: z.boolean(),
	/**
	* Opaque keyset cursor for the next page, or `null` at the end of the walk.
	* The key is the pod NAME (unique within `namespace`, which is constant for
	* a given `:dpId` — hence no compound key).
	*/
	nextCursor: z.string().nullable()
}).strict();
//#endregion
export { APP_PODS_DEFAULT_LIMIT, APP_PODS_MAX_LIMIT, POD_PROBLEMS, appPodContainerLastStateSchema, appPodContainerSchema, appPodSummarySchema, appPodsQuerySchema, appPodsResponseSchema, podAttributionView, podProblemEnum };

//# sourceMappingURL=app-pods.js.map